import React, { useState, useEffect, useRef, useActionState } from "react";
import { Editor } from "primereact/editor";
import {
  MDXEditor,
  MDXEditorMethods,
  headingsPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  tablePlugin,
  linkDialogPlugin,
  directivesPlugin,
  diffSourcePlugin,
  frontmatterPlugin,
  imagePlugin,
  listsPlugin,
  thematicBreakPlugin,
  BlockTypeSelect,
  InsertTable,
  InsertFrontmatter,
  InsertThematicBreak,
  CreateLink,
  ListsToggle,
} from "@mdxeditor/editor";
import { highlightPlugin } from "mdxe-plugin-highlight";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { AIButton } from "./AIButton";
import AIInputFloat from "./AIInputFloat";
// import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import Dropdown from "react-dropdown";
import CustomAlert from "./CustomAlert";
import { generateAiDocument } from "../Helpers/ApiChatGpt";
import { generateFocusAiDocument } from "../Helpers/ApiFocus";
import LinkPreview from "./LinkPreview";
import {
  getCompanyDataByCVR,
  getCVRdata,
  getCompanyDataByName,
} from "../Helpers/cvr";
import { useStore } from "../App/store";
import {
  generatePubMedDocument,
  getPubMedArticles,
} from "../Helpers/ApiPubMed";
import PdfViewer from "./PdfViewer";
import { stripHtmlFromLabel } from "../utils/textFormatting";
import { CgWebsite } from "react-icons/cg";
import { Dialog } from "primereact/dialog";
import { NodeData, KanbanCardData, Event, Address, Task } from "../types/types";
import ReactFlow from "@xyflow/react";
import type { Node } from "@xyflow/react";
import KanbanCard from "./KanbanCard";
import { updateNodesWithEditCard } from "./kanbanHelpers";
import EventCard from "./EventCard";
import AddressCard from "./AddressCard";

interface NotesModalProps {
  node: Node<NodeData>;
  nodes: Node<NodeData>[];
  isOpen: boolean;
  userRole?: "owner" | "editor" | "viewer" | null;
  handleSaveNotes: (
    notes: string,
    links: {
      title: string;
      url: string;
    }[],
  ) => void;
  handleCancelEditNotes: () => void;
  aiMode: boolean;
  setAIMode?: (mode: boolean) => void;
  handleAddUpdateEvent: (
    id: string,
    nodeTitle: string,
    title: string,
    description: string,
    date: Date | undefined,
  ) => void;
  handleDeleteEvent: (id: string) => void;
  handleAddUpdateAddress?: (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number,
  ) => void;
  handleDeleteAddress?: (id: string) => void;
  handleAddUpdateTask?: (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary?: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date,
  ) => void;
  handleDeleteTask?: (id: string | number) => void;
}

const NotesModal: React.FC<NotesModalProps> = ({
  node,
  nodes,
  isOpen,
  userRole,
  handleSaveNotes,
  handleCancelEditNotes,
  aiMode,
  setAIMode,
  handleAddUpdateEvent,
  handleDeleteEvent,
  handleAddUpdateAddress,
  handleDeleteAddress,
  handleAddUpdateTask,
  handleDeleteTask,
}) => {
  const [currentNotes, setCurrentNotes] = useState(node?.data.notes);
  const [currentLinkUrl, setCurrentLinkUrl] = useState(
    node?.data.links?.[0]?.url || "",
  );
  const quillRef = useRef<Editor | null>(null);
  const ref = React.useRef<MDXEditorMethods>(null);
  const [source, setSource] = useState("select source");
  const [topic, setTopic] = useState("");
  const [alertTitle, setAlertTitle] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAddLinkModal] = useState(false); // kept for legacy compat, no longer used
  const aiResponse = useStore.getState().aiResponse;
  const [editKanbanCard, setEditKanbanCard] = useState<KanbanCardData | null>(
    null,
  );
  const [kanbanCardItems, setKanbanCardItems] = useState<KanbanCardData[]>([]);
  const [showEventCard, setShowEventCard] = useState(false);
  const [showLocationCard, setShowLocationCard] = useState(false);
  const [showKanbanCard, setShowKanbanCard] = useState(false);
  const [showAIInput, setShowAIInput] = useState(false);

  // Quill editor configuration with custom toolbar
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
    "color",
    "background",
  ];

  // Helper function to convert markdown to HTML for initial load
  const convertMarkdownToHtml = (markdown: string): string => {
    if (!markdown) return "";

    // Check if content is already HTML (contains HTML tags)
    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(markdown);
    if (hasHtmlTags) {
      return markdown; // Already HTML, return as-is
    }

    // Helper function to apply inline formatting
    const applyInlineFormatting = (text: string): string => {
      return (
        text
          // Bold (must come before italic to handle *** correctly)
          .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
          .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
          .replace(/__(.+?)__/g, "<strong>$1</strong>")
          // Italic
          .replace(/\*(.+?)\*/g, "<em>$1</em>")
          .replace(/_(.+?)_/g, "<em>$1</em>")
          // Links
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
          // Inline code
          .replace(/`([^`]+)`/g, "<code>$1</code>")
      );
    };

    let lines = markdown.split("\n");
    let html = "";
    let inList = false;
    let listType = "";

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      let processed = false;

      // Headers
      if (line.startsWith("### ")) {
        if (inList) {
          html += `</${listType}>`;
          inList = false;
        }
        html += `<h3>${applyInlineFormatting(line.substring(4))}</h3>`;
        processed = true;
      } else if (line.startsWith("## ")) {
        if (inList) {
          html += `</${listType}>`;
          inList = false;
        }
        html += `<h2>${applyInlineFormatting(line.substring(3))}</h2>`;
        processed = true;
      } else if (line.startsWith("# ")) {
        if (inList) {
          html += `</${listType}>`;
          inList = false;
        }
        html += `<h1>${applyInlineFormatting(line.substring(2))}</h1>`;
        processed = true;
      }
      // Unordered list items
      else if (line.match(/^[\*\-]\s+(.+)$/)) {
        const match = line.match(/^[\*\-]\s+(.+)$/);
        if (match) {
          if (!inList || listType !== "ul") {
            if (inList) html += `</${listType}>`;
            html += "<ul>";
            inList = true;
            listType = "ul";
          }
          html += `<li>${applyInlineFormatting(match[1])}</li>`;
          processed = true;
        }
      }
      // Ordered list items
      else if (line.match(/^\d+\.\s+(.+)$/)) {
        const match = line.match(/^\d+\.\s+(.+)$/);
        if (match) {
          if (!inList || listType !== "ol") {
            if (inList) html += `</${listType}>`;
            html += "<ol>";
            inList = true;
            listType = "ol";
          }
          html += `<li>${applyInlineFormatting(match[1])}</li>`;
          processed = true;
        }
      }
      // Empty line
      else if (line.trim() === "") {
        if (inList) {
          html += `</${listType}>`;
          inList = false;
        }
        html += "<br/>";
        processed = true;
      }

      // Regular paragraph content
      if (!processed && line.trim()) {
        if (inList) {
          html += `</${listType}>`;
          inList = false;
        }
        html += `<p>${applyInlineFormatting(line)}</p>`;
      }
    }

    // Close any open list
    if (inList) {
      html += `</${listType}>`;
    }

    return html;
  };

  useEffect(() => {
    if (isOpen) {
      const notes = node?.data.notes || "";
      const htmlNotes = convertMarkdownToHtml(notes);
      setCurrentNotes(htmlNotes);
      setCurrentLinkUrl(node?.data.links?.[0]?.url || "");

      // Position cursor at the end of the Quill editor content
      setTimeout(() => {
        if (quillRef.current) {
          const quill = (quillRef.current as any).getQuill();
          if (quill) {
            const length = quill.getLength();
            quill.setSelection(length, 0);
            quill.focus();
          }
        }
      }, 100);
    }
  }, [isOpen, node?.data.notes, node?.data.links]);

  useEffect(() => {
    if (typeof aiResponse === "string" && aiResponse.trim() !== "") {
      setCurrentNotes(aiResponse);
      useStore.getState().setAIResponse("");
    }
  }, [aiResponse]);

  // Ensure MDXEditor updates when currentNotes changes
  useEffect(() => {
    if (ref.current && typeof currentNotes === "string") {
      ref.current.setMarkdown(currentNotes);
    }
  }, [currentNotes]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCancelEditNotes();
      }

      // Ctrl/Cmd + S to save notes
      if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleSaveNotesAndLinks();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    } else {
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleCancelEditNotes, currentNotes, currentLinkUrl]);

  const handleSaveNotesAndLinks = async () => {
    const notesToSave = currentNotes || "";
    const links = currentLinkUrl.trim()
      ? [{ title: "", url: currentLinkUrl.trim() }]
      : [];
    handleSaveNotes(notesToSave, links);
  };

  const handleCancel = () => {
    handleCancelEditNotes();
    setCurrentNotes("");
  };

  // function handleSaveKanbanCard(
  //   event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  // ): void {
  //   if (!editKanbanCard) return;

  //   // Update the kanbanCardItems state with the edited card
  //   setKanbanCardItems((prevItems) =>
  //     prevItems.map((item) =>
  //       item.id === editKanbanCard.id ? { ...item, ...editKanbanCard } : item
  //     )
  //   );

  //   // Optionally, update the parent data if needed
  //   if (handleAddUpdateTask) {
  //     // Find the corresponding node and update its data
  //     const updatedNodes = updateNodesWithEditCard(nodes, editKanbanCard);
  //     handleAddUpdateTask(updatedNodes);
  //   }

  //   setEditKanbanCard(null);
  // }

  const switchToNotesTab = (key: string) => {
    const tabs = document.querySelectorAll(".react-tabs__tab");
    const notesTab = Array.from(tabs).find((tab) =>
      tab.textContent?.toLowerCase().includes(key),
    );
    if (notesTab) {
      (notesTab as HTMLElement).click();
    }
  };

  let result: any;
  let cvrResult = "";
  let htmlResult = "";
  const getInsightsFromSource = async () => {
    if (topic?.trim() === "") {
      showAlert("Notice", "Please enter a topic to get insights.");
      return;
    }
    switchToNotesTab("notes");

    switch (source.toLowerCase()) {
      case "cvr":
        setLoading(true);
        //cvrResult = (await getCVRinfo(topic)) ?? null;
        if (!isNaN(Number(topic))) {
          cvrResult = await getCompanyDataByCVR(topic);
        } else {
          cvrResult = await getCompanyDataByName(topic);
        }
        const cvrData = cvrResult || "No data received from CVR.";
        setCurrentNotes(cvrData);
        setLoading(false);
        break;
      case "tinglysning":
      case "statstidende":
      case "retsinformation":
      case "biq":
        showAlert(
          "Notice",
          `The ${source.toUpperCase()} integration is work in progress.`,
        );
        break;
      case "chatgpt":
        setLoading(true);
        result = await generateAiDocument(topic);

        let bodyContent = result.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
        setCurrentNotes(bodyContent ? bodyContent[1] : result);
        setLoading(false);
        break;
      case "focusai":
        setLoading(true);
        result = await generateFocusAiDocument(topic);
        //bodyContent = result.match(/<body[^>]*>((.|[\n\r])*)<\/body>/im);
        setCurrentNotes(result ? result.toString() : "");
        setLoading(false);
        break;
      case "pubmed":
        setLoading(true);
        // Step 1: Get Article Ids
        result = await getPubMedArticles(topic);
        //console.log("PubMed search result", result);
        const textResult = result.join(",");
        //setCurrentNotes(textResult);

        // Step 2: Get Article Details
        const response = await generatePubMedDocument(result);
        //console.log("PubMed document result", response.data);
        htmlResult = typeof response === "string" ? response : response.data;
        // console.info("HTML Result", htmlResult);

        // Parse the XML string
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(htmlResult, "text/xml");

        const htmlContainer = document.createElement("div");

        // Extract data
        const articles = xmlDoc.querySelectorAll("PubmedArticle");
        articles.forEach((article) => {
          const pmid = article.querySelector("PMID")?.textContent || "";
          const journalTitle =
            article.querySelector("Journal > Title")?.textContent || "";
          const articleTitle =
            article.querySelector("ArticleTitle")?.textContent || "";
          const abstractSections =
            article.querySelectorAll("AbstractText") || [];

          // Create HTML elements for each article
          const articleContainer = document.createElement("div");
          articleContainer.innerHTML = `
            <h1>${articleTitle}</h1>
            <p><strong>Journal:</strong> ${journalTitle}</p>
            <p><strong>PMID:</strong> ${pmid}</p>
            <h2>Abstract</h2>
          `;

          // Append abstract sections
          abstractSections.forEach((section) => {
            const label = section.getAttribute("Label") || "Section";
            const text = section.textContent;
            const sectionElem = document.createElement("p");
            sectionElem.innerHTML = `<strong>${label}:</strong> ${text}`;
            sectionElem.innerHTML += "<br/><br/>";
            articleContainer.appendChild(sectionElem);
          });

          articleContainer.innerHTML += "<br/><br/><br/>";

          htmlContainer.appendChild(articleContainer);
        });

        setCurrentNotes(htmlContainer.innerHTML);

        setLoading(false);
        break;
      default:
        showAlert("Notice", "Please select a source of knowledge.");
        break;
    }
  };

  const showAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
  };

  if (!isOpen) {
    return null;
  }

  const handleSetAIButton = (value: boolean) => {
    if (setAIMode) {
      setAIMode(value);
    }
    // Show/hide the AI input float based on toggle state
    setShowAIInput(value);
  };

  const addUpdateEvent = (
    id: string,
    nodeTitle: string,
    title: string,
    description: string,
    date: Date,
  ) => {
    if (!title || !date) {
      alert("Title and date are required to save the event.");
      return;
    }
    handleAddUpdateEvent(
      node.id,
      nodeTitle,
      title,
      description || "",
      date || new Date(),
    );
    setShowEventCard(false);
  };

  const deleteEvent = (id: string) => {
    handleDeleteEvent(id);
    setShowEventCard(false);
  };

  const addUpdateAddress = async (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number,
  ) => {
    if (!name || !street || !zip_code || !city) {
      alert(
        "Name, street, zip code, and city fields are required to save the address.",
      );
      return;
    }
    handleAddUpdateAddress(
      node.id,
      nodeTitle,
      name,
      street,
      zip_code,
      city,
      Phone,
      email,
      vat,
      employees,
      notes,
      latitude,
      longitude,
    );
    setShowLocationCard(false);
  };

  const deleteAddress = (id: string) => {
    handleDeleteAddress(id);
    setShowLocationCard(false);
  };

  const addUpdateTask = (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary?: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date,
  ): Task => {
    if (!title || !startDate || !endDate) {
      alert("Title and dates are required to save the event.");
      return;
    }
    handleAddUpdateTask(
      node.id,
      nodeTitle,
      title,
      status,
      summary,
      priority,
      tags,
      assignee,
      estimate,
      rankId,
      color,
      className,
      startDate,
      endDate,
    );
    setShowKanbanCard(false);
  };

  const deleteTask = (id: string) => {
    handleDeleteTask(id);
    setShowKanbanCard(false);
  };

  return (
    // <PrimeReactProvider value={{ zIndex: { modal: 1, overlay: 2 } }}>
    <Dialog
      visible={isOpen}
      onHide={handleCancel}
      header={stripHtmlFromLabel(node?.data.label || "Notes")}
      className="notes-modal-dialog"
      style={{
        width: "50vw",
        height: "66vh",
        minWidth: "300px",
        minHeight: "400px",
      }}
      closable={true}
      modal
      blockScroll
      resizable={true}
      onResizeEnd={(e) => {
        // Dialog is resizable
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* <div className="notes-modal-insights">
          <div className="source-dropdown-item">
            <Dropdown
              data-tooltip="Source of knowledge"
              key={"source-dropdown"}
              className="source-dropdown"
              options={useStore.getState().sourceOptions}
              value={source}
              onChange={(source) => setSource(source.value)}
              placeholder="select source"
            />
          </div>

          <div>
            <input
              className="topic-input"
              type="text"
              placeholder={`Ask ${source ? source : "select source"}...`}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  getInsightsFromSource();
                }
              }}
            />
          </div>

          <div className="menu-item narrow">
            <Button
              icon={
                <i
                  className="pi pi-graduation-cap"
                  style={{ fontSize: "1.2em" }}
                />
              }
              className="insight-source-button"
              style={{ width: "2.1em", height: "1.15em" }}
              onClick={getInsightsFromSource}
              type="button"
              tooltip="Get Insights"
              aria-label="Get Insights"
            />
          </div>
        </div> */}

        <div className="ai-button-container">
          <AIButton aiMode={aiMode} handleSetAIButton={handleSetAIButton} />
        </div>

        <TabView className="notes-tabview">
          <TabPanel
            header={
              <span style={{ display: "flex", alignItems: "center" }}>
                <i
                  className="pi pi-align-left"
                  style={{ marginRight: "0.5em" }}
                />
                Notes
              </span>
            }
          >
            <>
              <style>
                {`
                .notes-editor h1,
                .notes-editor h2,
                .notes-editor h3,
                .notes-editor h4 {
                margin-top: 0em !important;
                margin-bottom: 0.3em !important;
                padding-top: 0em !important;
                padding-bottom: 0em !important;
                }
                .notes-editor p,
                .notes-editor ul,
                .notes-editor ol,
                .notes-editor li,
                .notes-editor input[type="checkbox"] {
                font-size: 1em !important;
                margin-top: 0em !important;
                margin-bottom: 0.3em !important;
                padding-top: 0em !important;
                padding-bottom: 0em !important;
                }
                `}
              </style>

              <Editor
                ref={quillRef}
                className="notes-textarea"
                value={currentNotes}
                onTextChange={(e) => setCurrentNotes(e.htmlValue || "")}
                readOnly={userRole === "viewer"}
              />

              {/* Single URL input — replaces the old Links tab */}
              <div className="notes-link-row">
                <i className="pi pi-link notes-link-icon" />
                <input
                  className="notes-link-input"
                  type="url"
                  placeholder="Link URL (https://...)"
                  value={currentLinkUrl}
                  onChange={(e) => setCurrentLinkUrl(e.target.value)}
                  readOnly={userRole === "viewer"}
                />
                {currentLinkUrl && (
                  <a
                    href={currentLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="notes-link-open"
                    title="Open link"
                  >
                    <i className="pi pi-external-link" />
                  </a>
                )}
              </div>
            </>
          </TabPanel>
        </TabView>
        <div className="notes-modal-buttons">
          <button className="notes-button" onClick={handleSaveNotesAndLinks}>
            Save
          </button>
          <button className="notes-button" onClick={handleCancel}>
            Cancel
          </button>
          {/* {source.toLowerCase() === "cvr" && (
            <button
              className="map-button"
              onClick={() => handleMapData(currentNotes)}
            >
              Map the Data (beta)
            </button>
          )} */}
        </div>
      </div>
      {alertMessage && (
        <CustomAlert
          title={alertTitle}
          message={alertMessage}
          htmlMessage={null}
          onClose={() => setAlertMessage(null)}
        />
      )}
      {loading && (
        <div className="progress-overlay">
          <div className="progress-indicator"></div>
          <div className="working">
            <div>Insights</div>
            <div>coming up...</div>
          </div>
        </div>
      )}
      {showAddLinkModal && null /* legacy: link editing now inline */}
      {/* <KanbanCard
        visible={showKanbanCard}
        nodeTask={node?.data.task}
        setShowKanbanCard={setShowKanbanCard}
        handleAddUpdateTask={addUpdateTask}
        handleDeleteTask={deleteTask}
      />
      <EventCard
        visible={showEventCard}
        nodeEvent={node?.data.event}
        setShowEventCard={setShowEventCard}
        handleAddUpdateEvent={addUpdateEvent}
        handleDeleteEvent={deleteEvent}
      />
      <AddressCard
        visible={showLocationCard}
        nodeAddress={node?.data.address}
        setShowAddressCard={setShowLocationCard}
        handleAddUpdateAddress={addUpdateAddress}
        handleDeleteAddress={deleteAddress}
      /> */}
      <AIInputFloat
        visible={showAIInput}
        onClose={() => setShowAIInput(false)}
        onSubmit={(text) => console.log("AI Input submitted:", text)}
        nodes={nodes}
        edges={[]}
        selectedNodeId={node?.id || null}
        context="notes"
        onNotesResponse={(content, action = "append") => {
          // Add AI response to the notes
          if (quillRef.current) {
            const quill = (quillRef.current as any).getQuill();
            if (quill) {
              // Convert markdown to HTML - improved version
              const convertMarkdownToHtml = (markdown: string): string => {
                // Helper function to apply inline formatting
                const applyInlineFormatting = (text: string): string => {
                  return (
                    text
                      // Bold (must come before italic to handle *** correctly)
                      .replace(
                        /\*\*\*(.+?)\*\*\*/g,
                        "<strong><em>$1</em></strong>",
                      )
                      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                      .replace(/__(.+?)__/g, "<strong>$1</strong>")
                      // Italic
                      .replace(/\*(.+?)\*/g, "<em>$1</em>")
                      .replace(/_(.+?)_/g, "<em>$1</em>")
                      // Links
                      .replace(
                        /\[([^\]]+)\]\(([^)]+)\)/g,
                        '<a href="$2">$1</a>',
                      )
                      // Inline code
                      .replace(/`([^`]+)`/g, "<code>$1</code>")
                  );
                };

                let lines = markdown.split("\n");
                let html = "";
                let inList = false;
                let listType = "";

                for (let i = 0; i < lines.length; i++) {
                  let line = lines[i];
                  let processed = false;

                  // Headers
                  if (line.startsWith("### ")) {
                    if (inList) {
                      html += `</${listType}>`;
                      inList = false;
                    }
                    html += `<h3>${applyInlineFormatting(
                      line.substring(4),
                    )}</h3>`;
                    processed = true;
                  } else if (line.startsWith("## ")) {
                    if (inList) {
                      html += `</${listType}>`;
                      inList = false;
                    }
                    html += `<h2>${applyInlineFormatting(
                      line.substring(3),
                    )}</h2>`;
                    processed = true;
                  } else if (line.startsWith("# ")) {
                    if (inList) {
                      html += `</${listType}>`;
                      inList = false;
                    }
                    html += `<h1>${applyInlineFormatting(
                      line.substring(2),
                    )}</h1>`;
                    processed = true;
                  }
                  // Unordered list items
                  else if (line.match(/^[\*\-]\s+(.+)$/)) {
                    const match = line.match(/^[\*\-]\s+(.+)$/);
                    if (match) {
                      if (!inList || listType !== "ul") {
                        if (inList) html += `</${listType}>`;
                        html += "<ul>";
                        inList = true;
                        listType = "ul";
                      }
                      html += `<li>${applyInlineFormatting(match[1])}</li>`;
                      processed = true;
                    }
                  }
                  // Ordered list items
                  else if (line.match(/^\d+\.\s+(.+)$/)) {
                    const match = line.match(/^\d+\.\s+(.+)$/);
                    if (match) {
                      if (!inList || listType !== "ol") {
                        if (inList) html += `</${listType}>`;
                        html += "<ol>";
                        inList = true;
                        listType = "ol";
                      }
                      html += `<li>${applyInlineFormatting(match[1])}</li>`;
                      processed = true;
                    }
                  }
                  // Empty line
                  else if (line.trim() === "") {
                    if (inList) {
                      html += `</${listType}>`;
                      inList = false;
                    }
                    html += "<br/>";
                    processed = true;
                  }

                  // Regular paragraph content
                  if (!processed && line.trim()) {
                    if (inList) {
                      html += `</${listType}>`;
                      inList = false;
                    }

                    html += `<p>${applyInlineFormatting(line)}</p>`;
                  }
                }

                // Close any open list
                if (inList) {
                  html += `</${listType}>`;
                }

                return html;
              };

              const htmlContent = convertMarkdownToHtml(content);
              console.log("Converting to HTML:", {
                original: content,
                html: htmlContent,
              });

              if (action === "overwrite") {
                // Replace all content with HTML using Quill's dangerouslyPasteHTML
                quill.setText(""); // Clear first
                quill.clipboard.dangerouslyPasteHTML(0, htmlContent);
                const length = quill.getLength();
                quill.setSelection(length, 0);
              } else {
                // Append: Get current cursor position or end of document
                const cursorPosition =
                  quill.getSelection()?.index || quill.getLength();

                // Use dangerouslyPasteHTML to insert at position
                quill.clipboard.dangerouslyPasteHTML(
                  cursorPosition,
                  "<br/>" + htmlContent,
                );

                // Move cursor to the end
                setTimeout(() => {
                  const newLength = quill.getLength();
                  quill.setSelection(newLength, 0);
                }, 0);
              }
              quill.focus();
            }
          }
        }}
      />{" "}
    </Dialog>
  );
};

export default NotesModal;
